import { JSX, useEffect } from 'react';
import NextApp, { AppContext, AppProps } from 'next/app';
import { I18nProvider } from 'next-localization';
import { SitecorePageProps } from 'lib/page-props';

import '@ahc-digital/component-library/dist/component-library.css';
import Bootstrap from 'src/Bootstrap';
import 'assets/main.scss';

import { useRouter } from 'next/router';
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { reactPlugin } from 'src/shared/AppInsights.service';

function App({ Component, pageProps }: AppProps<SitecorePageProps>): JSX.Element {
  const { dictionary, ...rest } = pageProps;

  // Disable NextJS route prefetch via env variable
  const router = useRouter();
  useEffect(() => {
    // Fallback keeps behavior for pages that do not go through page-props-factory (e.g. 404/500).
    const disablePrefetch = pageProps.disablePrefetch ?? 'true';
    if (disablePrefetch === 'true') {
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      router.prefetch = async () => {};
    }
  }, [router, pageProps.disablePrefetch]);

  return (
    <AppInsightsContext.Provider value={reactPlugin}>
      <Bootstrap {...pageProps} />
      {/*
      // Use the next-localization (w/ rosetta) library to provide our translation dictionary to the app.
      // Note Next.js does not (currently) provide anything for translation, only i18n routing.
      // If your app is not multilingual, next-localization and references to it can be removed.
    */}
      <I18nProvider lngDict={dictionary} locale={pageProps.locale}>
        <Component {...rest} />
      </I18nProvider>
    </AppInsightsContext.Provider>
  );
}

// Defining getInitialProps here disables Automatic Static Optimization for pages/404 and pages/500
// (the only pages in this app still eligible for it), so _document - and ANALYTICS_SCRIPT_SOURCE - renders per-request for them too.
App.getInitialProps = async (appContext: AppContext) => {
  return NextApp.getInitialProps(appContext);
};

export default App;
